// utils/elementFormat/text.js
//
// The platform's TEXT CEILINGS (keepr-web docs/plans/data-types-second-pass.md
// §3.4, decision D6 as the owner ruled it on 2026-09-30): the longest value
// each text-shaped element type holds, enforced on every write path as 400
// `too_long { element, limit }` — the form, POST/PUT /api/items, bulk, batch
// ingest (a row error of the same code), multi add, quick add, a public intake
// form and the automation actions. Before this there was no ceiling on the
// item write path at all (a short text accepted a megabyte) while quick add
// statics capped at 2,000 and notes at 20,000: three answers to one question.
//
// PURE AND DEPENDENCY-FREE ON PURPOSE, and vendored byte-identical into
// keepr-web at `src/utils/elementFormat/text.js`, so the form's message, the
// element editor's "Up to 255 characters" and the API's 400 name the same
// number. MIRRORED in keepr-web — change them together, with the shared
// vectors in `vectors.json` beside this file. Nothing here may require()
// anything.
//
// The ECHO rule is the caller's: a value identical to the one already stored
// always saves (utils/typedElements.js), so a value that predates a ceiling
// never bricks the item that holds it. The read-only maintenance task
// `listOversizedTextValues` reports the stored values over a ceiling.
//
// PR 2 of the same program (§3.4, "text rules") adds the rules an AUTHOR sets
// on an element, beside the platform's ceiling:
//   maxLength   a short or long text's own limit, a whole number from 1 to
//               the element's ceiling — `too_long` with the element's limit,
//               the same code and count as the ceiling (lengthLimitFor).
//   case        'upper' | 'lower' on a short text: the value is STORED in
//               capitals / small letters (normalizeText).
//   trim        true on a short text: spaces before and after the value are
//               removed when it is stored (normalizeText).
// A short text's pattern lives beside this file, in pattern.js.

'use strict';

// Characters, counted as code points (countChars). The owner's ruling:
//   text-small  255 — a short text shows in titles, tiles and other tight
//               spaces, and a limit that low steers a longer value to the
//               type that fits it.
//   text-large  20,000 (the card-level Notes number), 50,000 when the
//               element's `extendedLength` option is on.
//   rich-text   100,000 (about ten pages of markdown), 250,000 extended.
//   url         2,048, the longest URL every browser in use still opens
//               (utils/elementFormat/url.js MAX_LENGTH holds the same number).
const CEILINGS = Object.freeze({
	'text-small': 255,
	'text-large': 20000,
	'rich-text': 100000,
	url: 2048,
});

// The ceilings an element reaches with `options.extendedLength: true`. Only
// the two long types have one: a short text that needs more is a long text,
// and a link is a link.
const EXTENDED_CEILINGS = Object.freeze({
	'text-large': 50000,
	'rich-text': 250000,
});

// The option that switches an element to its extended ceiling.
const EXTENDED_OPTION = 'extendedLength';

// The types an author may give a `maxLength` of their own: a rich text is
// markdown, whose length a person does not see, and a link is a link.
const LIMITABLE = Object.freeze(['text-small', 'text-large']);
// The write-time normalisations a short text may carry (options.case,
// options.trim) — only a short text: a paragraph's spacing and a markdown
// document's capitals are the writer's.
const NORMALIZABLE = Object.freeze(['text-small']);
const CASES = Object.freeze(['upper', 'lower']);

const toStr = v => (v === null || v === undefined ? '' : String(v));
const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

/** True when the data type has an extended ceiling an element may opt into. */
function canExtend(dataType) {
	return hasOwn(EXTENDED_CEILINGS, dataType);
}

/**
 * ceilingFor(dataType, options?) -> number | null
 *
 * The ceiling one element is held to: its type's, or the extended one when
 * the element's options carry `extendedLength: true` and the type has one.
 * null for a type that has no ceiling. `options` is the element's options
 * object (or any object carrying the key — a flattened schema element works).
 */
function ceilingFor(dataType, options) {
	if (!hasOwn(CEILINGS, dataType)) return null;
	if (options && options[EXTENDED_OPTION] === true && canExtend(dataType)) return EXTENDED_CEILINGS[dataType];
	return CEILINGS[dataType];
}

/**
 * countChars(text) -> number
 *
 * Characters as a person counts them: a code point, so an emoji is one
 * character and not the two UTF-16 units JavaScript's `.length` reports.
 * The same count JSON Schema's `maxLength` and Mongo's `$strLenCP` use.
 */
function countChars(text) {
	const s = toStr(text);
	let n = s.length;
	for (let i = 0; i < s.length - 1; i++) {
		const c = s.charCodeAt(i);
		if (c >= 0xd800 && c <= 0xdbff) {
			const d = s.charCodeAt(i + 1);
			if (d >= 0xdc00 && d <= 0xdfff) { n--; i++; }
		}
	}
	return n;
}

/**
 * isOverCeiling(value, dataType, options?) -> boolean
 *
 * True when a STRING value is longer than the element's ceiling. Anything
 * that is not a string, and any type without a ceiling, is never over —
 * shape is the caller's question.
 */
function isOverCeiling(value, dataType, options) {
	const limit = ceilingFor(dataType, options);
	if (limit === null || typeof value !== 'string') return false;
	// A UTF-16 length within the limit cannot hold more code points than
	// that, so the count only runs for a value that might be over.
	if (value.length <= limit) return false;
	return countChars(value) > limit;
}

/** True when an author may set `maxLength` on the data type. */
function canLimit(dataType) {
	return LIMITABLE.indexOf(dataType) !== -1;
}

/** True when the data type takes `case` and `trim`. */
function canNormalize(dataType) {
	return NORMALIZABLE.indexOf(dataType) !== -1;
}

/**
 * maxLengthOf(dataType, options?) -> number | null
 *
 * The author's own limit, when the type takes one and it is a whole number
 * from 1 to the element's ceiling; null otherwise. READ leniently: a value
 * the card write would refuse (0, 1.5, past the ceiling) binds nothing here,
 * and the ceiling still does.
 */
function maxLengthOf(dataType, options) {
	if (!canLimit(dataType) || !options) return null;
	const raw = options.maxLength;
	const ceiling = ceilingFor(dataType, options);
	if (typeof raw !== 'number' || !Number.isInteger(raw) || raw < 1 || ceiling === null || raw > ceiling) return null;
	return raw;
}

/**
 * lengthLimitFor(dataType, options?) -> number | null
 *
 * The limit one element's value is held to: the author's `maxLength` when
 * it set one, the ceiling otherwise. null for a type with neither.
 */
function lengthLimitFor(dataType, options) {
	const own = maxLengthOf(dataType, options);
	return own !== null ? own : ceilingFor(dataType, options);
}

/**
 * isOverLimit(value, dataType, options?) -> boolean
 *
 * isOverCeiling for the element's limit (lengthLimitFor): a string longer
 * than the author's `maxLength`, or than the ceiling when there is none.
 */
function isOverLimit(value, dataType, options) {
	const limit = lengthLimitFor(dataType, options);
	if (limit === null || typeof value !== 'string') return false;
	if (value.length <= limit) return false;
	return countChars(value) > limit;
}

/**
 * normalizeText(value, dataType, options?) -> value
 *
 * A short text as it is STORED: spaces before and after removed under
 * `trim: true`, then capitals or small letters under `case`. Anything that
 * is not a string, and every other type, comes back unchanged.
 */
function normalizeText(value, dataType, options) {
	if (typeof value !== 'string' || !canNormalize(dataType) || !options) return value;
	let out = value;
	if (options.trim === true) out = out.trim();
	if (options.case === 'upper') out = out.toUpperCase();
	else if (options.case === 'lower') out = out.toLowerCase();
	return out;
}

/** A limit as a person reads it: 1000 -> '1,000'. */
function formatLimit(limit) {
	return String(limit).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

module.exports = {
	CEILINGS,
	EXTENDED_CEILINGS,
	EXTENDED_OPTION,
	LIMITABLE,
	NORMALIZABLE,
	CASES,
	canExtend,
	canLimit,
	canNormalize,
	ceilingFor,
	maxLengthOf,
	lengthLimitFor,
	countChars,
	isOverCeiling,
	isOverLimit,
	normalizeText,
	formatLimit,
};
